/** Mock数据Provider */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type {
  MockDataContextType, DashboardStats, Order, Container, ContainerSubstep,
  IoTDevice, IoTAlert, FinanceReceivable, FieldTask, Buyer,
  SystemConfig, RBACRole, AuditLog, SensitiveOperation, PermissionMatrixRow,
} from '@bluplanet/shared';
import {
  mockDashboard, mockOrders, mockContainers, mockContainerSubsteps,
  mockIoTDevices, mockIoTAlerts, mockFinances,
  mockTasks, mockBuyers, mockSystemConfigs,
  mockRBACRoles, mockAuditLogs, mockSensitiveOps, mockPermissionMatrix,
} from '@bluplanet/shared';
import { randomFloat } from '@bluplanet/shared';

const MockDataContext = createContext<MockDataContextType>({
  dashboard: mockDashboard,
  orders: mockOrders,
  containers: mockContainers,
  iotDevices: mockIoTDevices,
  iotAlerts: mockIoTAlerts,
  finances: mockFinances,
  tasks: mockTasks,
  buyers: mockBuyers,
  systemConfigs: mockSystemConfigs,
  rbacRoles: mockRBACRoles,
  auditLogs: mockAuditLogs,
  sensitiveOps: mockSensitiveOps,
  permissionMatrix: mockPermissionMatrix,
  containerSubsteps: mockContainerSubsteps,
  setContainerSubsteps: () => {},
  setContainers: () => {},
  refreshData: () => {},
});

export function MockDataProvider({ children }: { children: React.ReactNode }) {
  const [dashboard, setDashboard] = useState<DashboardStats>(mockDashboard);
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [containers, setContainers] = useState<Container[]>(mockContainers);
  const [containerSubsteps, setContainerSubsteps] = useState<ContainerSubstep[]>(mockContainerSubsteps);
  const [iotDevices, setIoTDevices] = useState<IoTDevice[]>(mockIoTDevices);
  const [iotAlerts, setIoTAlerts] = useState<IoTAlert[]>(mockIoTAlerts);
  const [finances, setFinances] = useState<FinanceReceivable[]>(mockFinances);
  const [tasks, setTasks] = useState<FieldTask[]>(mockTasks);
  const [buyers, setBuyers] = useState<Buyer[]>(mockBuyers);
  const [systemConfigs, setSystemConfigs] = useState<SystemConfig[]>(mockSystemConfigs);
  const [rbacRoles, setRBACRoles] = useState<RBACRole[]>(mockRBACRoles);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>(mockAuditLogs);
  const [sensitiveOps, setSensitiveOps] = useState<SensitiveOperation[]>(mockSensitiveOps);
  const [permissionMatrix, setPermissionMatrix] = useState<PermissionMatrixRow[]>(mockPermissionMatrix);

  const refreshData = useCallback((module: string) => {
    switch (module) {
      case 'dashboard':
        setDashboard({ ...mockDashboard, revenueThisMonth: randomFloat(1800000, 2200000) });
        break;
      case 'orders':
        setOrders([...mockOrders]);
        break;
      case 'containers':
        setContainers([...mockContainers]);
        break;
      case 'substeps':
        setContainerSubsteps([...mockContainerSubsteps]);
        break;
      case 'iot':
        setIoTDevices([...mockIoTDevices]);
        setIoTAlerts([...mockIoTAlerts]);
        break;
      case 'finances':
        setFinances([...mockFinances]);
        break;
      case 'tasks':
        setTasks([...mockTasks]);
        break;
      case 'buyers':
        setBuyers([...mockBuyers]);
        break;
      case 'system':
        setSystemConfigs([...mockSystemConfigs]);
        setRBACRoles([...mockRBACRoles]);
        setAuditLogs([...mockAuditLogs]);
        setSensitiveOps([...mockSensitiveOps]);
        setPermissionMatrix([...mockPermissionMatrix]);
        break;
      case 'all':
        setDashboard({ ...mockDashboard, revenueThisMonth: randomFloat(1800000, 2200000) });
        setOrders([...mockOrders]);
        setContainers([...mockContainers]);
        setContainerSubsteps([...mockContainerSubsteps]);
        setIoTDevices([...mockIoTDevices]);
        setIoTAlerts([...mockIoTAlerts]);
        setFinances([...mockFinances]);
        setTasks([...mockTasks]);
        setBuyers([...mockBuyers]);
        setSystemConfigs([...mockSystemConfigs]);
        setRBACRoles([...mockRBACRoles]);
        setAuditLogs([...mockAuditLogs]);
        setSensitiveOps([...mockSensitiveOps]);
        setPermissionMatrix([...mockPermissionMatrix]);
        break;
    }
  }, []);

  const value = useMemo<MockDataContextType>(() => ({
    dashboard,
    orders,
    containers,
    iotDevices,
    iotAlerts,
    finances,
    tasks,
    buyers,
    systemConfigs,
    rbacRoles,
    auditLogs,
    sensitiveOps,
    permissionMatrix,
    containerSubsteps,
    setContainerSubsteps,
    setContainers,
    refreshData,
  }), [
    dashboard, orders, containers, iotDevices, iotAlerts,
    finances, tasks, buyers, systemConfigs, rbacRoles,
    auditLogs, sensitiveOps, permissionMatrix, containerSubsteps,
    setContainerSubsteps, setContainers, refreshData,
  ]);

  return (
    <MockDataContext.Provider value={value}>
      {children}
    </MockDataContext.Provider>
  );
}

export function useMockData(): MockDataContextType {
  const context = useContext(MockDataContext);
  if (!context) {
    throw new Error('useMockData must be used within MockDataProvider');
  }
  return context;
}

export default MockDataContext;
